import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Heart, ShoppingBag } from 'lucide-react';
import { formatPrice } from '../../utils/formatPrice';
import { useCart } from '../../hooks/useCart';
import { useWishlist } from '../../hooks/useWishlist';

const Card = styled(Link)`
  display: block;
  cursor: pointer;
`;

const HoverImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.7s ease;
`;

const QuickAddBtn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.5s ${({ theme }) => theme.easeLuxe};
  background: hsla(0, 0%, 0%, 0.95);
  backdrop-filter: blur(8px);
  color: ${({ theme }) => theme.colors.cream};
  border: none;
  padding: 0.75rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${({ theme }) => theme.colors.champagne};
    color: ${({ theme }) => theme.colors.noir};
  }
`;

const MainImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.secondary};

  &:hover ${HoverImg} {
    opacity: 1;
  }

  &:hover ${QuickAddBtn} {
    transform: translateY(0);
  }

  &:hover ${MainImg} {
    transform: scale(1.05);
  }
`;

const Tags = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 1;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const WishBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 8px;
  border-radius: 999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsla(40, 30%, 97%, 0.9);
  backdrop-filter: blur(6px);
  z-index: 1;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    background 0.2s ease;

  ${Card}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.champagneDark};
  }
`;

const Body = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BrandRow = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Name = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.type.productName};
  font-weight: 400;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.foreground};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
`;

const Price = styled.span`
  font-size: ${({ theme }) => theme.type.price};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

const Original = styled.span`
  font-size: ${({ theme }) => theme.type.price};
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
`;

const SizesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding-top: 6px;
`;

const SizeChip = styled.span`
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 2px 6px;
  line-height: 1;
`;

const ExtraSizes = styled.span`
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ColorsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding-top: 8px;
`;

const ColorSwatch = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px hsla(0, 0%, 100%, 0.35);
`;

export function ProductCard({ product }) {
  const onSale =
    product.tags.includes('sale') && product.comparePrice != null;
  const pctOff =
    onSale &&
    Math.round((1 - product.price / product.comparePrice) * 100);

  const { add } = useCart();
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const inWishlist = isWishlisted(product.id);
  const sizes = Array.isArray(product.sizes) ? product.sizes : [];
  const maxSizesShown = 3;
  const visibleSizes = sizes.slice(0, maxSizesShown);
  const extraSizes = sizes.length - maxSizesShown;
  const colors = Array.isArray(product.colors) ? product.colors : [];

  const quickAdd = (e) => {
    e.preventDefault();
    if (!sizes.length || !colors.length) return;
    add({
      product,
      size: sizes[Math.floor(sizes.length / 2)],
      color: colors[0].name,
      qty: 1,
    });
  };

  return (
    <Card to={`/product/${product.slug}`} className="product-card">
      <Media className="product-card-image">
        <MainImg
          src={product.image}
          alt={product.name}
          loading="lazy"
          width={400}
          height={533}
          className="product-card-img"
        />
        {product.hoverImage && (
          <HoverImg src={product.hoverImage} alt="" aria-hidden loading="lazy" />
        )}
        <Tags>
          {product.tags.includes('new') && (
            <Chip style={{ background: '#000', color: '#f5f2eb' }}>New</Chip>
          )}
          {onSale && (
            <Chip style={{ background: '#524D46', color: '#f5f2eb' }}>-{pctOff}%</Chip>
          )}
          {product.tags.includes('limited') && (
            <Chip style={{ background: 'hsl(40, 30%, 96%)', color: '#000' }}>Limited</Chip>
          )}
          {product.tags.includes('bestseller') &&
            !product.tags.includes('new') && (
              <Chip style={{ background: 'hsl(0, 0%, 12%)', color: '#f5f2eb' }}>
                Bestseller
              </Chip>
            )}
        </Tags>
        <WishBtn
          type="button"
          aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          aria-pressed={inWishlist}
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(product.id);
          }}
        >
          <Heart size={16} strokeWidth={1.5} fill={inWishlist ? 'currentColor' : 'none'} />
        </WishBtn>
        <QuickAddBtn type="button" onClick={quickAdd}>
          <ShoppingBag size={14} /> Add to Bag
        </QuickAddBtn>
      </Media>
      <Body>
        <BrandRow>{product.collection}</BrandRow>
        <Name>{product.name}</Name>
        <PriceRow>
          <Price {...(onSale ? { style: { color: '#524D46' } } : {})}>{formatPrice(product.price)}</Price>
          {onSale && <Original>{formatPrice(product.comparePrice)}</Original>}
        </PriceRow>
        {sizes.length > 0 && (
          <SizesRow aria-label="Available sizes">
            {visibleSizes.map((size) => (
              <SizeChip key={size}>{size}</SizeChip>
            ))}
            {extraSizes > 0 && <ExtraSizes>+{extraSizes}</ExtraSizes>}
          </SizesRow>
        )}
        {colors.length > 0 && (
          <ColorsRow aria-label="Colours">
            {colors.slice(0, 6).map((c) => (
              <ColorSwatch key={c.name} style={{ backgroundColor: c.hex }} title={c.name} />
            ))}
          </ColorsRow>
        )}
      </Body>
    </Card>
  );
}
